<script lang="ts">
    import { type Attempt, type Puzzle, type Stats } from "@lib/types";
    import { formatTime, difficultyGroupName, isSolved, percentileBand } from "@lib/util";
    import { solve } from "@lib/api";

    interface Props {
        puzzle: Puzzle;
        attempt: Attempt;
        onStats?: (stats: Stats) => void;
    }

    let { puzzle, attempt, onStats }: Props = $props();

    const loadSummary = async (): Promise<Stats> => {
        const solution = isSolved(puzzle, attempt.placements);
        if (!solution) throw new Error("Invalid solution");

        const solveTime = attempt.timer.elapsed;
        const stats = await solve(attempt.session, solution, solveTime);
        onStats?.(stats);
        return stats;
    };

    const percentileLabel = (p: number): string => {
        if (p >= 90) return "Incredible";
        if (p >= 75) return "Impressive";
        if (p >= 50) return "Well done";
        return "Solved";
    };
</script>

<section>
    <div class="oswald fade-in summary">
        {#await loadSummary()}
            <div class="headline">
                <p class="headline-value monospace pulse">##:##</p>
                <p class="headline-label pulse">Loading results...</p>
            </div>
            <div class="details">
                <div class="stat">
                    <p>Puzzle</p>
                    <div class="spacer"></div>
                    <p class="pulse">####</p>
                </div>
                <div class="stat">
                    <p>Difficulty</p>
                    <div class="spacer"></div>
                    <p class="pulse">####</p>
                </div>
                <div class="stat">
                    <p>Your time</p>
                    <div class="spacer"></div>
                    <p class="pulse">#:##</p>
                </div>
                <div class="stat">
                    <p>Average time</p>
                    <div class="spacer"></div>
                    <p class="pulse">#:##</p>
                </div>
            </div>
        {:then stats}
            <div class="headline">
                {#if stats.percentile > 0}
                    <p class="headline-label">{percentileLabel(stats.percentile)}</p>
                    <p class="headline-value monospace">{percentileBand(stats.percentile)}</p>
                {:else}
                    <p class="headline-label">Solved</p>
                    <p class="headline-value monospace">{formatTime(stats.solve_time)}</p>
                {/if}
            </div>
            <div class="details">
                <div class="stat">
                    <p>Puzzle</p>
                    <div class="spacer"></div>
                    <p>#{puzzle.id}</p>
                </div>
                <div class="stat">
                    <p>Difficulty</p>
                    <div class="spacer"></div>
                    <p>{difficultyGroupName(puzzle.difficulty)}</p>
                </div>
                <div class="stat">
                    <p>Your time</p>
                    <div class="spacer"></div>
                    <p>{formatTime(stats.solve_time)}</p>
                </div>
                <div class="stat">
                    <p>Average time</p>
                    <div class="spacer"></div>
                    <p>{formatTime(stats.avg_time)}</p>
                </div>
                {#if stats.hints_used > 0}
                    <div class="stat">
                        <p>Hints used</p>
                        <div class="spacer"></div>
                        <p>{stats.hints_used}</p>
                    </div>
                {/if}
            </div>
        {:catch}
            Failed to load stats.
        {/await}
    </div>
</section>

<style>
    .summary {
        width: 272px;
    }

    .headline {
        text-align: center;
        padding: 0.75rem 0.5rem;
        margin-bottom: 0.5rem;
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: 8px;
        background-color: rgba(0, 0, 0, 0.05);
    }

    .headline-label {
        font-size: 0.85rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        margin-bottom: 0.15rem;
    }

    .headline-value {
        font-size: 2.4rem;
        line-height: 1.1;
    }

    .details {
        padding: 0.5rem;
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: 8px;
        background-color: rgba(0, 0, 0, 0.05);
    }

    .stat {
        display: flex;
    }

    .stat .spacer {
        flex-grow: 1;
        border-bottom: 2px dotted var(--black);
        margin-bottom: 4px;
    }
</style>
